import * as React from 'react';
import styled from '@emotion/styled';
import MaskedInput from 'react-text-mask';
import * as vars from '../../../consts/variables';
import { scrollElementIntoView } from '../../../helpers/scroll';
import { TestSelector } from '../../../helpers/TestSelector';
import { css } from '@emotion/react';

const styles: Record<string, React.CSSProperties> = {
    wrapper: {
        margin: '0 15px 20px',
        justifyContent: 'center',
        alignItems: 'center',
        position: 'relative',
        marginTop: 0,
    },
    input: {
        width: '100%',
        padding: 10,
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        appearance: 'none',
        height: 40,
        fontSize: 16,
        opacity: 1,
        border: `1px solid ${vars.gray}`,
        color: `${vars.black}`,
        background: 'transparent',
    },
    inputText: {
        opacity: 1,
    },
    inputDate: {
        top: -40,
        position: 'relative',
        opacity: 0,
    },
};

export const MuiLikeStyledLabel = css`
    font-size: 14px;
    padding: 0 5px;
    margin-left: 10px;
    display: inline-block;
    line-height: 14px;

    &:after {
        content: ' ';
        width: 100%;
        height: 2px;
        background: #fff;
        position: absolute;
        left: 0;
        top: calc(50% - 1px);
        z-index: -1;
    }
`;

const StyledTopLabelInput = styled.label<{ isError?: boolean }>`
    width: 100%;
    span {
        font-weight: 400;
        position: relative;
        z-index: 1;
        top: 8px;
        margin-left: 10px;
        ${MuiLikeStyledLabel}
    }

    input,
    textarea {
        display: block;
        width: 100%;
        height: 48px;
        border: 2px solid
            ${(props) =>
                props.isError ? props.theme.colors.watermelon[100] : props.theme.colors.iron[100]};
        background: #fff;
        border-radius: 8px;
        font-weight: 400;
        font-size: 16px;
        padding: 4px 12px;

        &:focus {
            /* remove outline, but keep visual information that input is in focus */
            outline: 0;
            border-color: ${(props) => props.theme.colors.azure[50]};
        }
    }

    textarea {
        padding: 12px;
        height: auto;
    }
`;

interface IProps {
    className?: string;
    value: string;
    placeholder: string;
    isError?: boolean;
    type?: string;
    onChange(event: React.ChangeEvent<any>): void;
    wrapperStyle?: any;
    inputStyle?: React.CSSProperties;
    isFirstInvalidFormField?: boolean;
    testSelectorName?: string;
    mask?: any;
    maskPlaceholder?: string;
    muiStyling?: boolean;
    rows?: number;
    cols?: number;
}

interface IState {
    placeholder: string;
}

export class Input extends React.Component<IProps, IState> {
    constructor(props) {
        super(props);

        this.state = {
            placeholder: this.props.placeholder,
        };
    }

    inputRef = null;
    getInputRef = (ref) => (this.inputRef = ref);

    handleFocus = () => {
        this.setState({ placeholder: this.props.maskPlaceholder || this.props.placeholder });
    };

    handleBlur = () => {
        this.setState({ placeholder: this.props.placeholder });
    };

    shouldComponentUpdate(nextProps: IProps, nextState: IState) {
        const {
            value,
            placeholder,
            isError,
            type,
            wrapperStyle,
            isFirstInvalidFormField,
            mask,
            maskPlaceholder,
        } = this.props;
        // 👀 I'm sure there's something to be said here
        return (
            isError !== nextProps.isError ||
            isFirstInvalidFormField !== nextProps.isFirstInvalidFormField ||
            value !== nextProps.value ||
            placeholder !== nextState.placeholder ||
            type !== nextProps.type ||
            wrapperStyle !== nextProps.wrapperStyle ||
            mask !== nextProps.mask ||
            this.state.placeholder !== nextState.placeholder ||
            maskPlaceholder !== nextProps.maskPlaceholder
        );
    }

    componentDidUpdate() {
        if (this.props.isFirstInvalidFormField && this.props.isError) {
            scrollElementIntoView(this.inputRef);
        }
    }

    getInput(props) {
        return this.props.type === 'textarea' ? (
            <textarea rows={this.props.rows} cols={this.props.cols} {...props} />
        ) : (
            <input {...props} />
        );
    }

    render() {
        const {
            placeholder,
            isError,
            type,
            value,
            onChange,
            wrapperStyle,
            mask,
            maskPlaceholder,
            inputStyle,
            testSelectorName,
        } = this.props;
        const inputStyleDefault = isError
            ? { ...styles.input, border: `1px solid ${vars.pink}` }
            : styles.input;
        const inputType = type || 'text';

        if (mask) {
            return (
                <div ref={this.getInputRef} style={{ ...styles.wrapper, ...wrapperStyle }}>
                    <MaskedInput
                        type={inputType}
                        style={inputStyleDefault}
                        value={value}
                        placeholder={this.state.placeholder}
                        onChange={onChange}
                        onFocus={maskPlaceholder ? this.handleFocus : null}
                        onBlur={maskPlaceholder ? this.handleBlur : null}
                        mask={mask}
                        {...TestSelector.getSelector(testSelectorName)}
                    />
                </div>
            );
        }

        return (
            <div
                ref={this.getInputRef}
                style={{ ...styles.wrapper, ...wrapperStyle }}
                className={this.props.className}
            >
                {this.props.muiStyling ? (
                    <StyledTopLabelInput isError={isError}>
                        {placeholder && <span> {placeholder} </span>}

                        {this.getInput({
                            type: inputType,
                            value,
                            onChange,
                            ...TestSelector.getSelector(testSelectorName),
                        })}
                    </StyledTopLabelInput>
                ) : (
                    this.getInput({
                        type: inputType,
                        style: { ...inputStyleDefault, ...inputStyle },
                        value,
                        placeholder,
                        onChange,
                        ...TestSelector.getSelector(testSelectorName),
                    })
                )}
            </div>
        );
    }
}
